import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow, Rectangle } from '@react-google-maps/api';
import { 
  Car, Phone, Star, MapPin, RefreshCw, Plus, Users, 
  Ticket, Clock, Zap, X, Check, Printer, Search, Truck, User, FileText, Navigation
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_OPTIONS, AIRPORT_TERMINAL, GOOGLE_MAPS_API_KEY } from '../../config/googleMaps';
import ThermalReceipt from './ThermalReceipt';
import ActiveOrdersTable from './ActiveOrdersTable';
import AssignDriverModal from './AssignDriverModal';
import NewRideNotification from './NewRideNotification';
import { getCarMarkerIcon, getLocationPinIcon, CAR_STATUS_COLORS } from '../../utils/carMarkers';
import { useSettings } from '../../context/SettingsContext';
import useWebSocket from '../../hooks/useWebSocket';
import { getLocationAddress, getRideDestination, getRidePickup } from '../../utils/rideFormat';
import { getAdAttribution } from '../../utils/adSource';

// Queue zone bounds - rectangle at the entrance where taxis enter
// Positioned to the RIGHT of Rruga e Aeroportit, not touching the road
const QUEUE_ZONE_BOUNDS = {
  north: 41.41920,   // North boundary (extended up)
  south: 41.41800,   // South boundary (extended down)
  east: 19.71400,    // East boundary 
  west: 19.71290     // West boundary (to the right of airport road)
};

const mapContainerStyle = {
  width: '100%',
  height: '100%',
  minHeight: '400px'
};

const mapRegularRideStatusForDispatch = (status) => {
  if (status === 'accepted') return 'assigned';
  if (status === 'arriving') return 'en_route';
  return status;
};

const normalizeExternalRideForDispatch = (ride) => {
  const priceLek = ride?.price_lek ?? ride?.final_price ?? ride?.estimated_price ?? ride?.fare ?? 0;
  return {
    ...ride,
    dispatch_type: 'regular',
    ticket_number: ride?.ride_number || `APP-${String(ride?.id || '').slice(0, 6).toUpperCase()}`,
    pickup: ride?.pickup || (ride?.pickup_address ? { address: ride.pickup_address } : null),
    destination: ride?.dropoff?.address || ride?.destination?.address || ride?.destination || 'Destinacion N/A',
    dropoff: ride?.dropoff || ride?.destination || (ride?.destination_address ? { address: ride.destination_address } : null),
    passenger_count: ride?.passengers_count || ride?.passenger_count || 1,
    price_lek: priceLek,
    price_eur: (ride?.price_eur ?? (priceLek / 100)),
    vehicle_plate: ride?.vehicle?.plate || ride?.vehicle_plate,
    status: mapRegularRideStatusForDispatch(ride?.status),
  };
};

const mapLegacyWebsiteBookingToRide = (message) => ({
  id: message.booking_id,
  ticket_number: message.booking_id ? `WEB-${String(message.booking_id).slice(0, 6).toUpperCase()}` : 'WEB',
  passenger_name: message.customer_name || 'Pasagjer',
  passenger_phone: message.customer_phone || '',
  passenger_email: message.customer_email || '',
  pickup: { address: message.pickup_address || 'Pikë nisje' },
  destination: message.destination_address || 'Destinacion',
  dropoff: { address: message.destination_address || 'Destinacion' },
  passenger_count: 1,
  price_lek: message.price_lek ?? message.estimated_price ?? 0,
  price_eur: message.price_eur ?? 0,
  estimated_price: message.estimated_price ?? message.price_lek ?? 0,
  status: 'pending',
  source: 'website',
  dispatch_type: 'regular',
});

const AirportMapView = () => {
  // Get date formatting from settings
  const { formatDateTime, formatTime } = useSettings();
  
  // Map state
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  
  // Queue state
  const [driverQueue, setDriverQueue] = useState([]);
  const [queueStats, setQueueStats] = useState({ total_in_queue: 0, avg_wait_minutes: 0 });
  
  // Rides state
  const [rides, setRides] = useState([]);
  const [stats, setStats] = useState({
    total_rides_today: 0, pending_rides: 0, active_rides: 0, 
    completed_rides: 0, revenue_eur: 0, revenue_lek: 0
  });
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTicket, setShowTicket] = useState(false);
  const [ticketData, setTicketData] = useState(null);
  const [showThermalReceipt, setShowThermalReceipt] = useState(false);
  
  // Assign Driver Modal
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [rideToAssign, setRideToAssign] = useState(null);
  
  // New Ride Notification
  const [showNewRideNotification, setShowNewRideNotification] = useState(false);
  const [newRideData, setNewRideData] = useState(null);
  
  // Route presets
  const [routePresets, setRoutePresets] = useState([]);
  const [searchDestination, setSearchDestination] = useState('');
  const [selectedPreset, setSelectedPreset] = useState(null);
  
  // Google Places suggestions
  const [googleSuggestions, setGoogleSuggestions] = useState([]);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    passenger_type: 'walk-in',
    passenger_name: '',
    passenger_phone: '',
    passenger_count: 1,
    destination: '',
    destination_lat: null,
    destination_lng: null,
    vehicle_type: 'sedan',
    price_eur: 0,
    price_lek: 0,
    notes: ''
  });

  // Loading
  const [loading, setLoading] = useState(true);
  
  // WebSocket for real-time updates
  const { isConnected } = useWebSocket('dispatcher', (message) => {
    console.log('AirportMapView received WebSocket message:', message);
    const incomingRide = message.ride || message.data;
    
    if (message.type === 'new_airport_ride_auto_assigned') {
      // Auto-assigned ride - show success toast instead of notification popup
      toast.success(message.message || `Porosi u caktua automatikisht tek ${message.ride?.driver_name}`, {
        duration: 5000,
      });
      fetchData(); // Refresh the list to show assigned ride
    } else if (message.type === 'new_ride' || message.type === 'new_airport_ride') {
      // Manual assignment needed - show notification popup
      const normalizedRide = incomingRide
        ? (incomingRide?.source && incomingRide?.source !== 'dispatcher_panel'
          ? normalizeExternalRideForDispatch(incomingRide)
          : incomingRide)
        : null;
      setNewRideData(normalizedRide);
      setShowNewRideNotification(true);
      fetchData();
    } else if (message.type === 'new_website_booking') {
      // Backend sends full ride via `new_ride` first; don't overwrite popup with legacy payload.
      fetchData();
    } else if (message.type === 'ride_updated' || message.type === 'ride_assigned') {
      fetchData();
    } else if (message.type === 'queue_update') {
      fetchData();
    } else if (
      message.type === 'driver_status_changed' ||
      message.type === 'driver_location' ||
      message.type === 'driver.location.updated' ||
      message.type === 'driver_moved'
    ) {
      fetchData();
    }
  });
  
  // Google Maps
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES
  });

  const center = useMemo(() => AIRPORT_TERMINAL, []);

  // Initialize Google Places services when map is loaded
  useEffect(() => {
    if (isLoaded && window.google && window.google.maps && window.google.maps.places) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
      placesService.current = new window.google.maps.places.PlacesService(document.createElement('div'));
    }
  }, [isLoaded]);

  // Search Google Places
  const searchGooglePlaces = (input) => {
    setSearchDestination(input);
    
    if (!input || input.length < 2 || !autocompleteService.current) {
      setGoogleSuggestions([]);
      return;
    }

    autocompleteService.current.getPlacePredictions(
      { 
        input,
        componentRestrictions: { country: 'al' }, // Albania
        types: ['geocode', 'establishment']
      },
      (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setGoogleSuggestions(predictions);
        } else {
          setGoogleSuggestions([]);
        }
      }
    );
  };

  // Select Google Place
  const selectGooglePlace = (placeId, description) => {
    if (!placesService.current) return;

    placesService.current.getDetails(
      { placeId, fields: ['geometry', 'name', 'formatted_address'] },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          const destination = place.formatted_address || description;
          
          // Try to find a matching preset for suggested price
          const matchingPreset = routePresets.find(preset => {
            const presetDest = (preset.dropoff?.address || preset.name || '').toLowerCase();
            const selectedDest = destination.toLowerCase();
            // Check if destination contains preset name or vice versa
            return presetDest.includes(selectedDest.split(',')[0].toLowerCase()) || 
                   selectedDest.includes(presetDest.replace('aeroport → ', ''));
          });
          
          setFormData(prev => ({
            ...prev,
            destination: destination,
            destination_lat: place.geometry.location.lat(),
            destination_lng: place.geometry.location.lng(),
            // Use matching preset price or default to 0
            price_eur: matchingPreset?.price_eur || Math.round((matchingPreset?.price_lek || 0) / 100) || 0,
            price_lek: matchingPreset?.price_lek || matchingPreset?.price || 0
          }));
          setSearchDestination('');
          setGoogleSuggestions([]);
          setSelectedPreset(matchingPreset || null);
        }
      }
    );
  };

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // First, sync queue based on driver proximity
      try {
        await api.post('/airport/queue/sync-proximity');
      } catch (e) {
        console.log('Queue sync skipped:', e.message);
      }
      
      const [driversRes, ridesRes, appWebRidesRes, queueRes, queueStatsRes, presetsRes] = await Promise.all([
        api.get('/drivers?limit=100'),
        api.get('/airport/rides?limit=100'),
        api.get('/rides?status=pending,accepted,arriving,in_progress&limit=200'),
        api.get('/airport/queue'),
        api.get('/airport/queue/stats'),
        api.get('/routes/presets?limit=100')
      ]);

      const airportRides = ridesRes.data || [];
      const appWebsiteRides = (appWebRidesRes.data || [])
        .filter((ride) => ride?.source === 'passenger_app' || ride?.source === 'website')
        .map((ride) => normalizeExternalRideForDispatch(ride));

      const allRides = [...airportRides, ...appWebsiteRides]
        .sort((a, b) => new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime());
      setDrivers(driversRes.data.filter(d => d.position));
      setRides(allRides);
      
      // Calculate stats from rides data for consistency
      const pendingCount = allRides.filter(r => r.status === 'pending').length;
      const assignedCount = allRides.filter(r => r.status === 'assigned' || r.status === 'en_route').length;
      const completedCount = allRides.filter(r => r.status === 'completed').length;
      const totalRevenue = allRides
        .filter(r => r.status === 'completed')
        .reduce((sum, r) => sum + ((r.price_eur || 0) || ((r.price_lek || 0) / 100)), 0);
      
      setStats({
        total_rides_today: allRides.length,
        pending_rides: pendingCount,
        active_rides: assignedCount,
        completed_rides: completedCount,
        revenue_eur: totalRevenue.toFixed(0),
        revenue_lek: (totalRevenue * 100).toFixed(0)
      });
      
      setDriverQueue(queueRes.data);
      setQueueStats(queueStatsRes.data);
      setRoutePresets(presetsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 5 seconds for real-time updates
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Filter presets
  const filteredPresets = routePresets.filter(preset => 
    preset.name?.toLowerCase().includes(searchDestination.toLowerCase()) ||
    preset.dropoff?.address?.toLowerCase().includes(searchDestination.toLowerCase())
  );

  // Handle preset selection
  const handleSelectPreset = (preset) => {
    setSelectedPreset(preset);
    setFormData(prev => ({
      ...prev,
      destination: preset.dropoff?.address || preset.name?.replace('Aeroport → ', ''),
      destination_lat: preset.dropoff?.position?.lat,
      destination_lng: preset.dropoff?.position?.lng,
      price_eur: preset.price_eur || Math.round((preset.price_lek || 0) / 100),
      price_lek: preset.price_lek || preset.price || preset.fixed_price || 0
    }));
    setSearchDestination('');
  };

  // Create ride
  const handleCreateRide = async (e) => {
    e.preventDefault();
    if (!formData.destination) {
      alert('Ju lutem zgjidhni një destinacion');
      return;
    }
    
    // Check token before making request
    const token = localStorage.getItem('token');
    console.log('Creating ride - Token exists:', !!token);
    if (!token) {
      alert('Sesioni ka skaduar. Ju lutem logohuni përsëri.');
      window.location.href = '/login';
      return;
    }
    
    try {
      console.log('Sending ride data:', formData);
      const response = await api.post('/airport/rides', formData);
      console.log('Ride created:', response.data);
      setTicketData(response.data);
      setShowTicket(true);
      setShowCreateModal(false);
      fetchData();
      // Reset form
      setFormData({
        passenger_type: 'walk-in', passenger_name: '', passenger_phone: '',
        passenger_count: 1, destination: '', destination_lat: null,
        destination_lng: null, vehicle_type: 'sedan', price_eur: 0, price_lek: 0, notes: ''
      });
      setSelectedPreset(null);
    } catch (error) {
      alert('Gabim: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Auto-assign first driver from queue
  const handleAutoAssign = async (rideId) => {
    try {
      const ride = rides.find((item) => item.id === rideId);

      if (ride?.dispatch_type === 'regular') {
        const queueDriverId = driverQueue.find((q) => drivers.some((d) => d.id === q.driver_id && d.status === 'online'))?.driver_id;
        const fallbackDriverId = drivers.find((d) => d.status === 'online')?.id;
        const selectedDriverId = queueDriverId || fallbackDriverId;

        if (!selectedDriverId) {
          alert('Nuk ka shoferë online për caktim');
          return;
        }

        await api.put(`/rides/${rideId}/assign?driver_id=${selectedDriverId}`);
        alert('✅ Shoferi u caktua me sukses');
      } else {
        const response = await api.post(`/airport/rides/${rideId}/auto-assign`);
        alert(`✅ ${response.data.message}`);
      }

      setShowNewRideNotification(false); // Close notification if open
      fetchData();
    } catch (error) {
      alert('Gabim: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Manual assign driver to ride
  const handleManualAssign = async (rideId, driverId) => {
    try {
      const ride = rides.find((item) => item.id === rideId);

      if (ride?.dispatch_type === 'regular') {
        await api.put(`/rides/${rideId}/assign?driver_id=${driverId}`);
        alert('✅ Shoferi u caktua me sukses');
      } else {
        const response = await api.put(`/airport/rides/${rideId}/assign`, { driver_id: driverId });
        alert(`✅ ${response.data.message || 'Shoferi u caktua me sukses'}`);
      }

      setShowAssignModal(false);
      setRideToAssign(null);
      setShowNewRideNotification(false);
      fetchData();
    } catch (error) {
      alert('Gabim: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Open assign modal
  const openAssignModal = (ride) => {
    setRideToAssign(ride);
    setShowAssignModal(true);
    setShowNewRideNotification(false); // Close notification if clicking from there
  };

  // Complete ride
  const handleCompleteRide = async (rideId) => {
    if (!window.confirm('Konfirmoni përfundimin?')) return;
    try {
      const ride = rides.find((item) => item.id === rideId);
      if (ride?.dispatch_type === 'regular') {
        await api.put(`/rides/${rideId}/status?status=completed`);
      } else {
        await api.put(`/airport/rides/${rideId}/complete`);
      }
      fetchData();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Cancel ride
  const handleCancelRide = async (rideId) => {
    if (!window.confirm('Anuloni udhëtimin?')) return;
    try {
      const ride = rides.find((item) => item.id === rideId);
      if (ride?.dispatch_type === 'regular') {
        await api.put(`/rides/${rideId}/status?status=cancelled`);
      } else {
        await api.put(`/airport/rides/${rideId}/cancel`);
      }
      fetchData();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Remove from queue
  const handleRemoveFromQueue = async (entryId) => {
    try {
      await api.delete(`/airport/queue/${entryId}`);
      fetchData();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Marker icons
  const getDriverMarkerIcon = (status, inQueue) => {
    // Use blue for queue, otherwise use standard status colors
    if (inQueue) {
      return {
        url: getCarMarkerIcon('online').url.replace('#22c55e', '#3b82f6').replace('#166534', '#1e40af').replace('#15803d', '#1d4ed8'),
        scaledSize: { width: 36, height: 36 },
        anchor: { x: 18, y: 18 }
      };
    }
    return getCarMarkerIcon(status);
  };

  const getAirportMarkerIcon = () => {
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="56" viewBox="0 0 48 56">
        <ellipse cx="24" cy="54" rx="10" ry="2" fill="rgba(0,0,0,0.2)"/>
        <path d="M24,0 C11,0 0,11 0,24 C0,42 24,54 24,54 C24,54 48,42 48,24 C48,11 37,0 24,0 Z" fill="#dc2313"/>
        <circle cx="24" cy="22" r="16" fill="#fff"/>
        <path d="M24 10 L27 18 L35 18 L29 23 L31 31 L24 26 L17 31 L19 23 L13 18 L21 18 Z" fill="#dc2313"/>
      </svg>
    `;
    return {
      url: `data:image/svg+xml,${encodeURIComponent(svg.trim())}`,
      scaledSize: { width: 48, height: 56 },
      anchor: { x: 24, y: 56 }
    };
  };

  // Check if driver is in queue
  const isDriverInQueue = (driverId) => {
    return driverQueue.some(q => q.driver_id === driverId);
  };

  // Get status info
  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-700',
      'assigned': 'bg-blue-100 text-blue-700',
      'en_route': 'bg-purple-100 text-purple-700',
      'arrived_pickup': 'bg-indigo-100 text-indigo-700',
      'passenger_onboard': 'bg-cyan-100 text-cyan-700',
      'in_progress': 'bg-amber-100 text-amber-700',
      'arrived_destination': 'bg-emerald-100 text-emerald-700',
      'completed': 'bg-green-100 text-green-700',
      'cancelled': 'bg-gray-100 text-gray-500'
    };
    return colors[status] || 'bg-gray-100';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Në Pritje', 'assigned': 'Caktuar', 'en_route': 'Tek Pasagjeri',
      'arrived_pickup': 'Tek Pickup', 'passenger_onboard': 'Pasagjeri Hipi',
      'in_progress': 'Në Udhëtim', 'arrived_destination': 'Tek Destinacioni',
      'completed': 'Përfunduar', 'cancelled': 'Anuluar'
    };
    return labels[status] || status;
  };

  if (loadError) {
    return <div className="p-8 text-center text-gray-500">Gabim në ngarkimin e hartës</div>;
  }

  if (!isLoaded) {
    return (
      <div className="p-8 text-center">
        <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
        <p className="mt-2 text-gray-500">Duke ngarkuar hartën...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 pb-4" style={{ minHeight: 'calc(100vh - 140px)' }}>
      {/* Top Stats Bar */}
      <div className="flex gap-3 flex-wrap">
        <div className="flex-1 min-w-[120px] bg-white rounded-lg p-3 border shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <Users className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-600">{queueStats.total_in_queue}</div>
            <div className="text-xs text-gray-500">Në Radhë</div>
          </div>
        </div>
        <div className="flex-1 min-w-[120px] bg-white rounded-lg p-3 border shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
            <Clock className="w-5 h-5 text-yellow-600" />
          </div>
          <div>
            <div className="text-2xl font-bold text-yellow-600">{stats.pending_rides}</div>
            <div className="text-xs text-gray-500">Në Pritje</div>
          </div>
        </div>
        <div className="flex-1 min-w-[120px] bg-white rounded-lg p-3 border shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
            <Check className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <div className="text-2xl font-bold text-green-600">{stats.completed_rides}</div>
            <div className="text-xs text-gray-500">Sot</div>
          </div>
        </div>
        <div className="flex-1 min-w-[120px] bg-white rounded-lg p-3 border shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
            <span className="text-green-600 font-bold">€</span>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-600">{stats.revenue_eur}</div>
            <div className="text-xs text-gray-500">Të Ardhura</div>
          </div>
        </div>
        
        {/* Create Ride Button */}
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-3 rounded-lg font-semibold transition shadow-lg"
          data-testid="new-ride-btn"
        >
          <Plus size={20} />
          Udhëtim i Ri
        </button>
      </div>

      {/* Main Content - Map + Sidebar */}
      <div className="flex gap-4" style={{ height: '400px' }}>
        {/* Map */}
        <div className="flex-1 bg-white rounded-xl overflow-hidden shadow-sm border relative">
          {/* Driver Stats Overlay */}
          <div className="absolute top-3 left-3 z-10 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border p-3 min-w-[160px]">
            <h4 className="text-xs font-bold text-gray-500 uppercase mb-2">Shoferët</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-green-500"></span>
                  <span className="text-sm text-gray-700">Online</span>
                </div>
                <span className="text-sm font-bold text-green-600">
                  {drivers.filter(d => d.status === 'online').length}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-yellow-500"></span>
                  <span className="text-sm text-gray-700">Në Rrugë</span>
                </div>
                <span className="text-sm font-bold text-yellow-600">
                  {drivers.filter(d => d.status === 'busy' || d.status === 'on_trip').length}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
                  <span className="text-sm text-gray-700">Në Radhë</span>
                </div>
                <span className="text-sm font-bold text-blue-600">
                  {queueStats.total_in_queue}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-gray-400"></span>
                  <span className="text-sm text-gray-700">Offline</span>
                </div>
                <span className="text-sm font-bold text-gray-500">
                  {drivers.filter(d => d.status === 'offline').length}
                </span>
              </div>
            </div>
          </div>

          <GoogleMap
            mapContainerStyle={{ ...mapContainerStyle, height: '100%' }}
            center={center}
            zoom={17}
            options={GOOGLE_MAPS_OPTIONS}
          >
            {/* Queue Zone - Horizontal Rectangle along main road */}
            <Rectangle
              bounds={QUEUE_ZONE_BOUNDS}
              options={{
                fillColor: '#22c55e',
                fillOpacity: 0.2,
                strokeColor: '#22c55e',
                strokeOpacity: 0.9,
                strokeWeight: 3,
                clickable: false
              }}
            />

            {/* Airport Terminal Marker */}
            <Marker
              position={AIRPORT_TERMINAL}
              icon={getAirportMarkerIcon()}
              title="TIA Terminal - Zona e Radhës"
              zIndex={1000}
            />

            {/* Driver Markers */}
            {drivers.map((driver) => (
              <Marker
                key={driver.id}
                position={{ lat: driver.position.lat, lng: driver.position.lng }}
                icon={getDriverMarkerIcon(driver.status, isDriverInQueue(driver.id))}
                onClick={() => setSelectedDriver(driver)}
                title={driver.name}
              />
            ))}

            {/* Driver Info Window */}
            {selectedDriver && (
              <InfoWindow
                position={{ lat: selectedDriver.position.lat, lng: selectedDriver.position.lng }}
                onCloseClick={() => setSelectedDriver(null)}
              >
                <div className="p-2 min-w-[180px]">
                  <div className="font-semibold">{selectedDriver.name}</div>
                  <div className="text-xs text-gray-500 mt-1">{selectedDriver.phone}</div>
                  <div className="text-xs text-gray-500">{selectedDriver.vehicle?.plate}</div>
                  <div className="mt-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      isDriverInQueue(selectedDriver.id) ? 'bg-blue-100 text-blue-600' :
                      selectedDriver.status === 'online' ? 'bg-green-100 text-green-600' :
                      selectedDriver.status === 'busy' ? 'bg-yellow-100 text-yellow-600' : 'bg-gray-100'
                    }`}>
                      {isDriverInQueue(selectedDriver.id) ? 'Në Radhë' :
                       selectedDriver.status === 'online' ? 'I Lirë' :
                       selectedDriver.status === 'busy' ? 'Në Udhëtim' : 'Offline'}
                    </span>
                  </div>
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        </div>

        {/* Right Sidebar - Queue + Rides */}
        <div className="w-[360px] flex flex-col gap-4 min-h-0">
          {/* Driver Queue */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-4 shadow-lg">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-white font-bold flex items-center gap-2">
                <Users size={18} />
                Radha ({queueStats.total_in_queue})
              </h3>
              <span className="text-blue-100 text-sm">{queueStats.avg_wait_minutes}&apos; pritje</span>
            </div>
            
            {driverQueue.length === 0 ? (
              <div className="text-blue-100 text-center py-4 text-sm">
                Nuk ka shoferë në radhë
              </div>
            ) : (
              <div className="space-y-2 max-h-[150px] overflow-y-auto">
                {driverQueue.slice(0, 5).map((entry) => (
                  <div key={entry.id} className="bg-white/20 rounded-lg p-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-white text-blue-600 flex items-center justify-center text-xs font-bold">
                        {entry.queue_position}
                      </div>
                      <div>
                        <div className="text-white text-sm font-medium">{entry.driver_name}</div>
                        <div className="text-blue-100 text-xs">{entry.vehicle_plate} • {entry.wait_time_minutes}&apos;</div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveFromQueue(entry.id)}
                      className="text-white/60 hover:text-white"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pending Rides */}
          <div className="flex-1 bg-white rounded-xl shadow-sm border overflow-hidden flex flex-col min-h-0">
            <div className="p-3 border-b bg-gray-50 flex justify-between items-center">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <Ticket size={18} className="text-yellow-600" />
                Udhëtime ({stats.pending_rides + stats.active_rides})
              </h3>
              <button onClick={fetchData} className="text-gray-400 hover:text-gray-600">
                <RefreshCw size={16} />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {rides.filter(r => ['pending', 'assigned', 'en_route'].includes(r.status)).length === 0 ? (
                <div className="p-6 text-center text-gray-400">
                  <Ticket className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Nuk ka udhëtime</p>
                </div>
              ) : (
                <div className="divide-y">
                  {rides.filter(r => ['pending', 'assigned', 'en_route'].includes(r.status)).map((ride) => {
                    const adSource = getAdAttribution(ride);
                    return (
                    <div key={ride.id} className="p-3 hover:bg-gray-50">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="font-mono text-xs text-gray-400">{ride.ticket_number}</div>
                          <div className="font-medium text-gray-900">{getRideDestination(ride)}</div>
                          <div className="text-xs text-gray-500">
                            Nga: {getRidePickup(ride)}
                          </div>
                          {(ride.source === 'passenger_app' || ride.source === 'website') && (
                            <div className="text-[10px] uppercase tracking-wide text-blue-600 font-semibold mt-0.5">
                              {ride.source === 'website' ? 'Website' : 'Passenger App'}
                            </div>
                          )}
                          {adSource && (
                            <div className={`inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ${adSource.className}`}>
                              {adSource.text}
                            </div>
                          )}
                          <div className="text-xs text-gray-500">
                            {ride.passenger_count} pasagjerë • {(ride.price_lek || (ride.price_eur || 0) * 100).toLocaleString()} L
                          </div>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(ride.status)}`}>
                          {getStatusLabel(ride.status)}
                        </span>
                      </div>
                      
                      {ride.driver_name && (
                        <div className="text-xs text-gray-500 mb-2">
                          {ride.taxi_id && <span className="text-red-600 font-medium">{ride.taxi_id}</span>} {ride.driver_name} • {ride.vehicle_plate}
                        </div>
                      )}
                      
                      <div className="flex gap-1">
                        {ride.status === 'pending' && (
                          <>
                            {queueStats.total_in_queue > 0 && (
                              <button
                                onClick={() => handleAutoAssign(ride.id)}
                                className="flex-1 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs rounded flex items-center justify-center gap-1"
                              >
                                <Zap size={12} /> Auto
                              </button>
                            )}
                            <button
                              onClick={() => handleCancelRide(ride.id)}
                              className="px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-600 text-xs rounded"
                            >
                              Anulo
                            </button>
                          </>
                        )}
                        {(ride.status === 'assigned' || ride.status === 'en_route') && (
                          <button
                            onClick={() => handleCompleteRide(ride.id)}
                            className="flex-1 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs rounded flex items-center justify-center gap-1"
                          >
                            <Check size={12} /> Përfundo
                          </button>
                        )}
                        <button
                          onClick={() => { setTicketData(ride); setShowTicket(true); }}
                          className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs rounded"
                        >
                          <Printer size={12} />
                        </button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Active Orders Table */}
      <div className="mt-4">
        <ActiveOrdersTable 
          rides={rides}
          queueHasDrivers={queueStats.total_in_queue > 0}
          onAutoAssign={handleAutoAssign}
          onAssign={openAssignModal}
          onCancel={handleCancelRide}
          onComplete={handleCompleteRide}
          onPrint={(ride) => { setTicketData(ride); setShowTicket(true); }}
        />
      </div>

      {/* Assign Driver Modal */}
      <AssignDriverModal
        isOpen={showAssignModal}
        onClose={() => { setShowAssignModal(false); setRideToAssign(null); }}
        ride={rideToAssign}
        onAssign={handleManualAssign}
        queueDrivers={driverQueue}
      />

      {/* New Ride Notification Popup */}
      <NewRideNotification
        isOpen={showNewRideNotification}
        ride={newRideData}
        onClose={() => setShowNewRideNotification(false)}
        onAssignAuto={handleAutoAssign}
        onAssignManual={openAssignModal}
        queueHasDrivers={queueStats.total_in_queue > 0}
      />

      {/* Create Ride Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-5 border-b sticky top-0 bg-white">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Car className="w-5 h-5 text-red-500" />
                Udhëtim i Ri
              </h2>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleCreateRide} className="p-5 space-y-4">
              {/* Walk-in label - no choice needed */}
              <div className="flex items-center gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <User className="w-5 h-5 text-orange-600" />
                <span className="font-medium text-orange-700">Pasagjer Walk-in</span>
              </div>

              {/* Passenger Info */}
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  value={formData.passenger_name}
                  onChange={(e) => setFormData({ ...formData, passenger_name: e.target.value })}
                  placeholder="Emri (opsional)"
                  className="px-3 py-2 border rounded-lg text-sm"
                />
                <input
                  type="tel"
                  value={formData.passenger_phone}
                  onChange={(e) => setFormData({ ...formData, passenger_phone: e.target.value })}
                  placeholder="Telefoni"
                  className="px-3 py-2 border rounded-lg text-sm"
                />
              </div>

              {/* Passenger Count */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Pasagjerë</label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => setFormData({ ...formData, passenger_count: num, vehicle_type: num > 4 ? 'van' : formData.vehicle_type })}
                      className={`w-9 h-9 rounded-lg font-semibold text-sm ${
                        formData.passenger_count === num ? 'bg-red-600 text-white' : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>

              {/* Destination */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Destinacioni</label>
                
                {formData.destination ? (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="font-medium text-green-800">{formData.destination}</div>
                        {formData.destination_lat && (
                          <div className="flex items-center gap-1 text-xs text-green-600 mt-1">
                            <Navigation size={12} />
                            GPS: {formData.destination_lat.toFixed(4)}, {formData.destination_lng.toFixed(4)}
                          </div>
                        )}
                      </div>
                      <button type="button" onClick={() => { 
                        setFormData({ ...formData, destination: '', destination_lat: null, destination_lng: null, price_eur: 0, price_lek: 0 }); 
                        setSelectedPreset(null); 
                      }} className="text-green-600 hover:text-green-800">
                        <X size={18} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Quick Presets */}
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {routePresets.slice(0, 6).map((preset) => (
                        <button
                          key={preset.id}
                          type="button"
                          onClick={() => handleSelectPreset(preset)}
                          className="px-2 py-2 text-left bg-gray-50 hover:bg-blue-50 border rounded-lg"
                        >
                          <div className="font-medium text-gray-800 text-xs truncate">
                            {preset.name?.replace('Aeroport → ', '')}
                          </div>
                          <div className="text-xs text-green-600 font-semibold">
                            {(preset.price_lek || (preset.price_eur || 0) * 100).toLocaleString()} L
                          </div>
                        </button>
                      ))}
                    </div>
                    
                    {/* Search with Google Maps */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={searchDestination}
                        onChange={(e) => searchGooglePlaces(e.target.value)}
                        placeholder="Kërko destinacion me Google Maps..."
                        className="w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm"
                      />
                      
                      {/* Dropdown with results */}
                      {searchDestination && (filteredPresets.length > 0 || googleSuggestions.length > 0) && (
                        <div className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-xl max-h-60 overflow-y-auto">
                          {/* Preset Routes First */}
                          {filteredPresets.length > 0 && (
                            <>
                              <div className="px-3 py-2 bg-yellow-50 text-xs font-semibold text-yellow-700 border-b">
                                ⭐ Çmime të Paracaktuara
                              </div>
                              {filteredPresets.slice(0, 4).map((preset) => (
                                <button
                                  key={preset.id}
                                  type="button"
                                  onClick={() => handleSelectPreset(preset)}
                                  className="w-full px-4 py-2.5 text-left hover:bg-yellow-50 flex justify-between items-center text-sm border-b"
                                >
                                  <div className="flex items-center gap-2">
                                    <MapPin size={14} className="text-yellow-500" />
                                    <span>{preset.name?.replace('Aeroport → ', '')}</span>
                                  </div>
                                  <span className="text-green-600 font-semibold">{(preset.price_lek || (preset.price_eur || 0) * 100).toLocaleString()} L</span>
                                </button>
                              ))}
                            </>
                          )}
                          
                          {/* Google Places */}
                          {googleSuggestions.length > 0 && (
                            <>
                              <div className="px-3 py-2 bg-blue-50 text-xs font-semibold text-blue-700 border-b">
                                🗺️ Google Maps
                              </div>
                              {googleSuggestions.map((suggestion) => (
                                <button
                                  key={suggestion.place_id}
                                  type="button"
                                  onClick={() => selectGooglePlace(suggestion.place_id, suggestion.description)}
                                  className="w-full px-4 py-2.5 text-left hover:bg-blue-50 flex items-center gap-2 text-sm border-b"
                                >
                                  <Navigation size={14} className="text-blue-500" />
                                  <span className="truncate">{suggestion.description}</span>
                                </button>
                              ))}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <Navigation size={12} />
                      Koordinatat ruhen automatikisht për navigacion te shoferi
                    </p>
                  </>
                )}
              </div>

              {/* Price - Always editable when destination is selected */}
              {formData.destination && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Çmimi EUR €</label>
                    <input
                      type="number"
                      value={formData.price_eur}
                      onChange={(e) => {
                        const eur = parseFloat(e.target.value) || 0;
                        setFormData({ ...formData, price_eur: eur, price_lek: Math.round(eur * 100) });
                      }}
                      className="w-full px-3 py-2.5 border rounded-lg font-semibold text-green-700 bg-green-50"
                      min="0"
                      step="1"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Çmimi LEK</label>
                    <input
                      type="number"
                      value={formData.price_lek}
                      onChange={(e) => setFormData({ ...formData, price_lek: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2.5 border rounded-lg"
                      min="0"
                    />
                  </div>
                </div>
              )}

              {/* Vehicle Type */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, vehicle_type: 'sedan' })}
                  disabled={formData.passenger_count > 4}
                  className={`flex-1 py-2.5 rounded-lg border-2 font-medium text-sm ${
                    formData.vehicle_type === 'sedan' ? 'border-gray-800 bg-gray-800 text-white' : 'border-gray-200'
                  } ${formData.passenger_count > 4 ? 'opacity-50' : ''}`}
                >
                  <Car className="w-4 h-4 mx-auto mb-1" />
                  Sedan
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, vehicle_type: 'van' })}
                  className={`flex-1 py-2.5 rounded-lg border-2 font-medium text-sm ${
                    formData.vehicle_type === 'van' ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-200'
                  }`}
                >
                  <Truck className="w-4 h-4 mx-auto mb-1" />
                  Van
                </button>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={!formData.destination || !formData.price_eur}
                className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2"
              >
                <Ticket size={18} />
                Krijo & Gjenero Ticket
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Ticket Modal */}
      {showTicket && ticketData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="p-6 text-center">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Car className="w-6 h-6 text-red-600" />
                <span className="text-xl font-bold">TAXI TICKET</span>
              </div>
              
              {/* Auto-assigned badge */}
              {ticketData.status === 'assigned' && ticketData.driver_name && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl">
                  <div className="flex items-center justify-center gap-2 text-green-700">
                    <Zap className="w-5 h-5" />
                    <span className="font-semibold">AUTO-CAKTUAR</span>
                  </div>
                  <p className="text-sm text-green-600 mt-1">
                    Shoferi i parë në radhë: <span className="font-bold">{ticketData.driver_name}</span>
                  </p>
                </div>
              )}
              
              <div className="text-3xl font-mono font-bold text-gray-900 mb-4">
                {ticketData.ticket_number}
              </div>
              
              <div className="space-y-2 text-left mb-4">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-500">Destinacioni</span>
                  <span className="font-semibold">{getLocationAddress(ticketData.destination) || ticketData.destination || '—'}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-500">Pasagjerë</span>
                  <span className="font-semibold">{ticketData.passenger_count}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-500">Automjeti</span>
                  <span className="font-semibold">{ticketData.vehicle_type === 'van' ? 'Van' : 'Sedan'}</span>
                </div>
                {/* Taxi ID + Plate */}
                {(ticketData.taxi_id || ticketData.vehicle_plate) && (
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-500">Taksia</span>
                    <span className="font-semibold">
                      {ticketData.taxi_id && <span className="text-red-600 mr-2">{ticketData.taxi_id}</span>}
                      {ticketData.vehicle_plate && <span>| {ticketData.vehicle_plate}</span>}
                    </span>
                  </div>
                )}
              </div>
              
              <div className="bg-green-50 rounded-xl p-4 mb-4 text-center">
                <div className="text-3xl font-bold text-green-700">{(ticketData.price_lek || (ticketData.price_eur || 0) * 100).toLocaleString()} L</div>
                <div className="text-sm text-green-600">{ticketData.price_lek?.toLocaleString()} LEK</div>
              </div>
              
              {/* Thermal Print Button - Main Action */}
              <button
                onClick={() => setShowThermalReceipt(true)}
                className="w-full bg-black hover:bg-gray-800 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 mb-3"
              >
                <Printer size={18} /> PRINTO FATURE
              </button>
              
              <button
                onClick={() => { setShowTicket(false); setTicketData(null); }}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 rounded-lg"
              >
                Mbyll
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Thermal Receipt Modal */}
      {showThermalReceipt && ticketData && (
        <ThermalReceipt
          ticketData={ticketData}
          printerWidth="80mm"
          onPrint={() => console.log('Thermal print triggered')}
          onClose={() => setShowThermalReceipt(false)}
        />
      )}
    </div>
  );
};

export default AirportMapView;
